import { useEffect, useMemo, useState } from 'react';

import { useChatSensor } from '@aicon-dev/caas-chat';
import { Avatar, Spinner } from '@aicon-dev/caas-ui';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { createGlobalStyle } from 'styled-components';

import apiClient from '~/config/apiClient';
import { useAuthContext } from '~/modules/auth/context/AuthProvider';
import { useNavigate } from '~/router';

import { Mask } from '../Mask';

import { createChannel, getUserData } from './apis';
import { ActionItemButton } from './styled';
import { container, dialog } from './styles.css';

const GlobalStyle = createGlobalStyle`
    html {
        overflow: hidden;
    }
`;

interface UserModalProps {
  userId: string;
  onClose?: () => void;
}

export const UserModal = (props: UserModalProps) => {
  const { userId, onClose } = props;
  const { userInfo } = useAuthContext();
  const { SendbirdInstance } = useChatSensor();
  const { t } = useTranslation('project_chat');
  const navigate = useNavigate();
  const myUserId = userInfo?._id || '';
  const [isCreating, setIsCreating] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const fetchUserData = async () => {
    const { data: userData } = await getUserData(userId);
    return userData;
  };

  const { data: user, isLoading } = useQuery({
    queryKey: ['fetch-chat-user-data', userId],
    queryFn: fetchUserData,
    enabled: !!userId,
  });

  const isMe = useMemo(() => {
    return userId === myUserId;
  }, [userId, myUserId]);

  const getChannels = async () => {
    const { data } = await apiClient.get<{ data: { channel_url: string; member_count: number }[] }>(
      `/sendbird/group-channels`,
      {
        params: {
          limit: 100,
          members_include_in: userId,
          metadata_key: 'type',
          metadata_values: 'GENERAL',
        },
      },
    );
    return data.data.filter((el) => el.member_count === 2);
  };

  const handleCreateChannel = async () => {
    console.log(SendbirdInstance, user);
    if (!SendbirdInstance || !user) return;

    try {
      setIsCreating(true);
      const channels = await getChannels();
      if (channels.length) {
        navigate('/chat/:chatId', { params: { chatId: channels[0].channel_url }, replace: true });
        setIsCreating(false);
      } else {
        const { data: newChannel } = await createChannel({
          type: 'GENERAL',
          channelName: user?.nickname,
          user_ids: [myUserId, userId],
        });
        const groupChannel = await SendbirdInstance.groupChannel.getChannel(newChannel._id);
        const messageData = user?.nickname || '';
        const inviteMessage = t('project_chat:channel_member_invite_message_format', {
          inviter: SendbirdInstance.currentUser?.nickname,
          members: messageData,
        });
        groupChannel
          .sendUserMessage({
            customType: 'invite',
            message: inviteMessage,
            data: messageData,
          })
          .onSucceeded(() => {
            setIsCreating(false);
            navigate('/chat/:chatId', {
              params: { chatId: groupChannel.url },
              replace: true,
            });
          });
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <GlobalStyle />
      <Mask
        visible={visible}
        onMaskClick={() => {
          setVisible(false);
        }}
        onDestroyed={() => {
          onClose?.();
        }}
      >
        <div className={container}>
          <motion.div
            className={dialog}
            animate="visible"
            initial="hidden"
            exit="hidden"
            variants={{
              hidden: {
                opacity: 0,
                scale: 0.9,
                y: 20,
              },
              visible: {
                opacity: 1,
                scale: 1,
                y: 0,
              },
            }}
          >
            {isLoading ? (
              <Skeleton />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem',
                }}
              >
                <Avatar size={90} src={user?.profile_url} name={user?.nickname} />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    color: '#2c384a',
                    fontWeight: 500,
                  }}
                >
                  {user?.metadata.companyName ? (
                    <div
                      style={{
                        color: '#414c5c',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {user?.metadata.companyName}
                    </div>
                  ) : null}
                  <div>{user?.nickname}</div>
                </div>
                {user?.is_online ? (
                  <div
                    style={{
                      color: '#1890FF',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    접속중
                  </div>
                ) : null}
                {!isMe ? (
                  <div>
                    <ActionItemButton color="#1890FF" onClick={handleCreateChannel}>
                      {isCreating ? (
                        <Spinner />
                      ) : (
                        <>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M21.8585 12.1242C21.8585 7.36011 17.4443 3.49805 11.9998 3.49805C6.55534 3.49805 2.14111 7.36011 2.14111 12.1242C2.14111 14.2931 3.05674 16.2771 4.56882 17.7929C4.44928 19.0449 4.05493 20.4177 3.61868 21.4479C3.52133 21.6771 3.70988 21.9334 3.95511 21.894C6.73526 21.438 8.38783 20.7381 9.10628 20.3733C10.0501 20.6251 11.023 20.7518 11.9998 20.7504C12.696 20.7504 13.3753 20.6873 14.0307 20.5672"
                              stroke="#1890FF"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                            <path
                              d="M19.5 14.25V21.75"
                              stroke="#1890FF"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                            <path
                              d="M15.75 18H23.25"
                              stroke="#1890FF"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />

                            <defs>
                              <clipPath id="clip0_89_42410">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span>채팅하기</span>
                        </>
                      )}
                    </ActionItemButton>
                  </div>
                ) : null}
              </div>
            )}
          </motion.div>
        </div>
      </Mask>
    </>
  );
};
